/* ============================================================
   Wiredan Vault: Push Notification Protocol Button
   File: src/components/NotificationButton.jsx
   Status: STABLE | VAPID BINARY SAFE | DYNAMIC LED INDICATOR
   ============================================================ */

import React, { useState, useEffect, useCallback } from 'react';
import { FiBell, FiLoader } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

// Safe Base64 to Uint8Array converter for browser PushManager API
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

const parseApiResponse = async (res) => {
  const text = await res.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    throw new Error(`Server returned non-JSON response (${res.status})`);
  }
};

const NotificationButton = () => {
  const { user, token } = useAuth();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkSubscription = useCallback(async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(Boolean(subscription));
    } catch {
      setIsSubscribed(false);
    }
  }, []);

  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  const togglePushSubscription = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return toast.error("Push Notifications are not supported on this device/browser.");
    }

    setLoading(true);
    const loadToast = toast.loading(isSubscribed ? "Disabling Notifications..." : "Enabling Notifications...");

    try {
      const registration = await navigator.serviceWorker.ready;
      const currentUserUuid = String(user?.uuid || user?.id || user?.userId || "").trim();

      if (isSubscribed) {
        // --- UNSUBSCRIBE PROTOCOL ---
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
          await subscription.unsubscribe();
          
          // Send unsubscribe request to the consolidated endpoint
          await fetch('/api/push/subscribe', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json', 
              ...(token ? { Authorization: `Bearer ${token}` } : {}) 
            },
            body: JSON.stringify({ 
              action: 'unsubscribe', 
              endpoint: subscription.endpoint, 
              user_uuid: currentUserUuid 
            })
          }).catch(() => {});

          // Fallback legacy endpoint call
          await fetch('/api/push/unsubscribe', {
            method: 'POST',
            headers: { 
              'Content-Type': 'application/json', 
              ...(token ? { Authorization: `Bearer ${token}` } : {}) 
            },
            body: JSON.stringify({ endpoint: subscription.endpoint, user_uuid: currentUserUuid })
          }).catch(() => {});
        }
        setIsSubscribed(false);
        toast.success("Push Notifications Disabled", { id: loadToast });
      } else {
        // --- SUBSCRIBE PROTOCOL ---
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') {
          throw new Error("Notification permission denied by user");
        }

        const resKey = await fetch('/api/push/vapid-key');
        const keyData = await parseApiResponse(resKey);
        
        if (!keyData.publicKey) {
          throw new Error("Failed to retrieve valid VAPID server key");
        }

        const applicationServerKey = urlBase64ToUint8Array(keyData.publicKey);
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey
        });

        const syncRes = await fetch('/api/push/subscribe', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            ...(token ? { Authorization: `Bearer ${token}` } : {}) 
          },
          body: JSON.stringify({ subscription, user_uuid: currentUserUuid })
        });

        const syncData = await parseApiResponse(syncRes);
        if (syncRes.ok && syncData.success) {
          setIsSubscribed(true);
          toast.success("Push Notifications Active!", { id: loadToast });
        } else {
          throw new Error(syncData.error || "Failed to register subscription on server");
        }
      }
    } catch (err) {
      toast.error(err.message || "Failed to toggle notifications", { id: loadToast });
    } finally {
      setLoading(false);
    }
  };

  return (
    <button 
      onClick={togglePushSubscription}
      disabled={loading}
      className="relative p-2.5 rounded-2xl bg-[#0B1221] text-white shadow-md active:scale-95 transition-all flex items-center justify-center border border-slate-800 hover:bg-slate-900"
      title={isSubscribed ? "Notifications Enabled" : "Notifications Disabled"}
    >
      {loading ? (
        <FiLoader className="w-5 h-5 animate-spin text-amber-400" />
      ) : (
        <FiBell className="w-5 h-5 text-slate-100" />
      )}
      
      {/* Dynamic LED Status Dot: Green = ON, Red = OFF */}
      <span 
        className={`absolute top-1.5 right-1.5 w-2.5 h-2.5 rounded-full border-2 border-[#0B1221] transition-colors duration-300 ${
          isSubscribed ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]' : 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]'
        }`} 
      />
    </button>
  );
};

export default NotificationButton;